<template>
    <v-row dense>
        <v-col :cols="canEdit ? 9 : 12" class="d-flex align-center">
            <span v-if="props.title" class="text-subtitle-2 text-grey font-weight-regular ml-4 mr-2">
                {{ props.title }}
            </span>
            <v-select
                v-if="!isEditing"
                v-model="quickFilterId"
                :loading="isLoading"
                :disabled="isLoading || !filterItems.length"
                :items="filterItems"
                :label="$t('quick_filter')"
                clearable
                hide-details
                variant="outlined"
                density="compact"
                color="primary"
                data-cy="filter"
                @update:model-value="changeFilter"
                @click:clear="clearFilters"
            />
            <v-text-field
                v-else
                v-model="newFilterName"
                :label="$t('filter_name')"
                :placeholder="$t('please_enter_a_new_name')"
                autofocus
                clearable
                hide-details
                variant="outlined"
                density="compact"
                color="primary"
                data-cy="filter-name"
            />
        </v-col>
        <v-col v-if="canEdit" cols="3" class="d-flex align-center">
            <template v-if="!isEditing">
                <v-btn
                    color="primary"
                    icon="mdi-pencil"
                    size="x-small"
                    variant="text"
                    data-cy="edit"
                    @click="isEditing = !isEditing"
                />
            </template>
            <template v-else>
                <v-btn
                    :loading="isLoading"
                    :disabled="isLoading || !newFilterName"
                    color="success"
                    icon="mdi-check"
                    size="x-small"
                    variant="text"
                    data-cy="save"
                    @click="save"
                />
                <v-btn
                    color="error"
                    icon="mdi-close"
                    size="x-small"
                    class="ml-2"
                    variant="text"
                    data-cy="close"
                    @click="isEditing = !isEditing"
                />
                <v-btn
                    color="error"
                    icon="mdi-delete-outline"
                    size="x-small"
                    class="ml-2"
                    variant="text"
                    data-cy="delete"
                    @click="deleteCustomFilter"
                />
            </template>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
    import type {SelectOptionType} from '~/utils/types/types'

    const props = defineProps<{
        title?: string
        path: string
        initDefaultFilters: Function
        companyType?: string[]
    }>()
    const quickFilterId = defineModel<string>({
        default: null,
    })
    const emit = defineEmits<{
        (e: 'change', value): void
        (e: 'reset'): void
    }>()

    const isLoading = ref<boolean>(false)
    const isEditing = ref<boolean>(false)
    const canEdit = ref<boolean>(false)
    const newFilterName = ref<string>('')
    const filters = ref(props.initDefaultFilters())
    const customFilters = ref([])
    const filterItems = ref<SelectOptionType[]>([])
    const person = ref(authStore.person)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadCustomFilters()
    })

    async function loadCustomFilters(): Promise<void> {
        isLoading.value = true
        try {
            let rq = await app.$api[props.path].get()
            if (props.companyType?.length) {
                rq = rq.filter(
                    el => el.customFilter?.companyType && el.customFilter.companyType.includes(props.companyType[0])
                )
            }
            customFilters.value = rq.filter(
                el =>
                    el.customFilter.creatorId === person.value.personId ||
                    (el.customFilter.companyId === person.value.companyId && el.customFilter.type === 'all')
            )
            filterItems.value = customFilters.value.map(value => ({value: value.id, title: value.customFilter.text}))
        } catch (e) {
            customFilters.value = []
        } finally {
            isLoading.value = false
        }
    }

    function changeFilter(val: string): void {
        canEdit.value = false
        const index = customFilters.value.findIndex(el => el.id === val)
        if (index >= 0) {
            filters.value = Object.assign(filters.value, customFilters.value[index].customFilter.filters)
            canEdit.value = customFilters.value[index].customFilter.creatorId === person.value.personId
        } else filters.value = props.initDefaultFilters()
        emit('change', filters.value)
    }

    function clearFilters(): void {
        quickFilterId.value = null
        isEditing.value = false
        canEdit.value = false
        filters.value = props.initDefaultFilters()
        emit('change', filters.value)
    }

    async function save(): Promise<void> {
        isLoading.value = true
        try {
            const data = customFilters.value.find(el => el.id === quickFilterId.value)
            data.customFilter.text = newFilterName.value

            await app.$api[props.path].post(data)
            await loadCustomFilters()
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('data_update_fail'))
        } finally {
            isLoading.value = false
            isEditing.value = false
            newFilterName.value = ''
        }
    }

    async function deleteCustomFilter(): Promise<void> {
        isLoading.value = true
        try {
            await app.$api[props.path].remove({id: quickFilterId.value})
            app.$toast.success(t('success_message.saved_successfully'))
            await loadCustomFilters()
        } catch (e) {
            app.$toast.error(t('data_update_fail'))
        } finally {
            clearFilters()
        }
    }

    defineExpose({
        loadCustomFilters,
    })
</script>
